/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the `.rdoc` modal document shell used by every overlay dialog.
 * @state-owner none
 * @allowed-imports React; overlay host
 * @forbidden business rules; network
 * @public-api workspace modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { ReactNode } from 'react';
import { useOverlay } from './OverlayHost';

/** The structure produced by `openOv()`. */
export function ModalDocument({
  title,
  subtitle,
  children,
  footer,
  maxWidth,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
  className?: string;
}) {
  const { closeModal } = useOverlay();
  return (
    <div className={`rdoc ${className ?? ''}`} style={maxWidth ? { maxWidth } : undefined}>
      <div className="rhd">
        <button className="cls5" type="button" onClick={closeModal}>
          ×
        </button>
        <h2>{title}</h2>
        {subtitle != null ? <p>{subtitle}</p> : null}
      </div>
      <div className="rbody">{children}</div>
      {footer ? <div className="rfoot">{footer}</div> : null}
    </div>
  );
}

/** The radio-style option button. */
export function OptionCard({
  on,
  title,
  description,
  onSelect,
}: {
  on: boolean;
  title: ReactNode;
  description: ReactNode;
  onSelect: () => void;
}) {
  return (
    <button className={`opt3 ${on ? 'on' : ''}`} type="button" onClick={onSelect}>
      <b>{title}</b>
      <span>{description}</span>
    </button>
  );
}

/** The section label. */
export function SectionLabel({
  children,
  first = false,
  style,
}: {
  children: ReactNode;
  first?: boolean;
  style?: Record<string, string | number>;
}) {
  return (
    <div className="sec5" style={{ ...(first ? { marginTop: 0 } : {}), ...(style ?? {}) }}>
      {children}
    </div>
  );
}
